import { BLOCK_PARAGRAPH, isBlockEmpty, moveToNextLeafBlock, stepper } from '@gitbook/doc-core';
import { type Change, isBlockRangeSelection, TextRange, type Value } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import {
    isBackspace,
    isDelete,
    isEnter,
    isModA,
    isModEnter,
    selectAllInContainerFirst,
} from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/**
 * Handle key down events for stepper blocks.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    if (isBlockRangeSelection(change.value.selection)) {
        return;
    }

    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isEnter(event)) {
        return onEnter(event, change);
    }

    if (isBackspace(event) || isDelete(event)) {
        return onDelete(event, change);
    }

    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, getStepRange);
    }

    return undefined;
}

/**
 * Pressing enter in a stepper block.
 */
function onEnter(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = stepper.getCurrentStepper(change.value);

    if (!current) {
        return undefined;
    }

    const lastBlock = current.step.blockNodes.last();

    // Last block is a paragraph and cursor is at the end of it.
    if (
        lastBlock &&
        lastBlock.type === BLOCK_PARAGRAPH &&
        isBlockEmpty(lastBlock) &&
        change.value.selection.hasAnchorAtEndOf(lastBlock)
    ) {
        // Remove last block.
        change.removeNodeByKey(lastBlock.key);

        // Insert a new step.
        const currentStepIndex = current.stepper.getChildIndex(current.step.key);
        const newStep = stepper.createStepperStep();
        change.insertNodeByKey(current.stepper.key, currentStepIndex + 1, newStep);
        change.collapseToStartOf(newStep);
        return change;
    }

    return undefined;
}

/**
 * Pressing backspace or delete in a stepper block.
 */
function onDelete(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = stepper.getCurrentStepper(change.value);

    if (!current) {
        return undefined;
    }

    const firstBlock = current.step.blockNodes.first();

    // There is only one empty node.
    if (
        current.step.nodes.size === 1 &&
        firstBlock &&
        isBlockEmpty(firstBlock) &&
        change.value.selection.hasAnchorAtStartOf(firstBlock)
    ) {
        change.removeNodeByKey(current.step.key);
        return change;
    }

    return undefined;
}

/**
 * Pressing mod+enter, exit the whole stepper block.
 */
function onModEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return;
    }

    const current = stepper.getCurrentStepper(change.value);
    if (!current) {
        return;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, current.stepper);
    return change;
}

function getStepRange(value: Value): TextRange | null {
    const current = stepper.getCurrentStepper(value);
    if (!current) {
        return null;
    }
    const firstText = current.step.getFirstText();
    const lastText = current.step.getLastText();
    if (!firstText || !lastText) {
        return null;
    }

    return TextRange.create().moveToRangeOf(firstText, lastText);
}
